// janus-engine/statuses/zone-add.ts — Janus lane A8 (2026-10-02, board R-065): the GM / sheet form's PERSISTENT-AoE add, decided in the
// browser. Twin of the deleted options/tools/statuses/add_status_effect.php zone block (its last family):
//   · the ZONE PLACEMENT around the target: a depth-bearing span `encounter_id|ox:oy:oz:ex:ey:ez` centred on the target's token
//     (get_token_center_xz: x_pos / z_pos, y = the floor-plane depth, missing means 0), sized so apply_status_effect's span→distance
//     override round-trips the ability's own aoe_distance (cube / square / line / cone span the side, every other shape the diameter);
//   · the INITIAL OCCUPANTS (owner rule 2026-07-15): every other roster creature / floor item already inside the zone gains the status now
//     (no rolls), with the legacy gates — creatures: on the map (a position), the team-type gates (persistent_aoe_team_type_allows_target:
//     opposing-only / same-team-only on the ability or its attack rider), never for an inventory-only zone; items: unless the ability
//     does not affect inventory or is opposing-only, never a map marker, only a placed row.
// The GM boundary places the anchor first, then selects initial occupants from that saved row's geometry.
//
// WORKER PICKS (owner to review): membership is the depth-bearing zone test (zone-membership.ts zoneContains — floor-plane x/y + the
// elevation band, the Self follow-aura expansion's own twin), not the retired XZ side-view test the endpoint still ran
// (creature_intersects_aoe over get_object_rectangle); a footprint the page cannot read (a swarm oblong, an unloaded mesh) is judged as
// the point it stands on.
import { SHAPES, type AoeOccupancy } from '../ability/aoe/expand-placed-aoe-targets.js';
import { zoneContains, ZoneHandBack, type Zone } from '../zones/zone-membership.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const scalar = (value: unknown): unknown => { let v = value; while (Array.isArray(v)) v = v[0]; return v; };
/** PHP empty(). */
const filled = (value: unknown): boolean => { const v = scalar(value); return v != null && v !== '' && v !== 0 && v !== '0' && v !== false; };
const SELF_RANGE = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const FRIEND = '7DBEB6D4-D147-703A-022E-05B290BB95C3';
const ENEMY = '9399856B-2132-7574-1684-91139A2831DD';
const SIDE_SPAN = new Set(['cube', 'square', 'line', 'cone']);

export interface ZoneAddInput {
  encounterId: string;
  /** The encounter blob the page holds (encounter_creatures_arr / encounter_inventories_arr). */
  encounter: Row;
  targetId: string;
  /** The caster row (the target itself unless the form names a creature). */
  caster: Row;
  definition: Row;
  layer: (row: Row, key: string) => unknown;
  /** ft_ground_occupancy_shape(row) — undefined when the page cannot read it. */
  occupancy: (row: Row) => AoeOccupancy | null | undefined;
}
export interface ZonePlacement { span: string; zone: Zone }

/** Does the definition carry a persistent-AoE zone (persistent_aoe + aoe_type both non-empty)? */
export function isZoneDefinition(def: Row, layer: (row: Row, key: string) => unknown): boolean {
  return filled(layer(def, 'persistent_aoe')) && filled(layer(def, 'aoe_type'));
}

/** pos_coord(): the position container only; absent / non-numeric = null. */
function posCoord(row: Row, key: 'x_pos' | 'y_pos' | 'z_pos'): number | null {
  const container = record(row.position) ?? record(row.pos_arr);
  if (!container || !(key in container)) return null;
  const v = scalar(container[key]);
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}
const creatureId = (row: Row): string => upper(record(row.encounter_creatures)?.encounter_creature_id);
const inventoryId = (row: Row): string => upper(record(row.inventory)?.inventory_id);
const rowsOf = (value: unknown): Row[] => Array.isArray(value) ? value.filter(v => record(v) !== null) as Row[] : [];

/** The roster row the target is (a creature first, then a floor item) — the legacy roster-only anchor lookup. */
export function zoneAnchorRow(encounter: Row, targetId: string): Row | null {
  const want = upper(targetId);
  return rowsOf(encounter.encounter_creatures_arr).find(r => creatureId(r) === want)
    ?? rowsOf(encounter.encounter_inventories_arr).find(r => inventoryId(r) === want) ?? null;
}

/** check_opposing_team(): both on the friend / enemy teams, and different. */
function opposing(caster: Row, target: Row, layer: ZoneAddInput['layer']): boolean {
  const team = (row: Row): string => {
    const bucket = record(row.encounter_creatures);
    const raw = bucket && 'team' in bucket ? bucket.team : layer(row, 'team');
    return upper(record(raw)?.items ? record(record(raw)?.items)?.item_id : scalar(raw));
  };
  const a = team(caster), b = team(target);
  return (a === FRIEND || a === ENEMY) && (b === FRIEND || b === ENEMY) && a !== b;
}

/** Placement for a persistent-AoE add whose target stands on the encounter roster; null for a plain add. */
export function resolveZonePlacement(input: ZoneAddInput): ZonePlacement | null {
  const { encounter, definition: def, layer } = input;
  const anchor = zoneAnchorRow(encounter, input.targetId);
  if (!anchor) return null;
  const targetId = upper(input.targetId);
  const position = record(anchor.position) ?? {};
  const n = (v: unknown): number => { const x = Number(scalar(v)); return Number.isFinite(x) ? x : 0; };
  // get_token_center_xz(): x_pos / z_pos, else the anchor's own origin point, else 0; y = the floor-plane depth (missing means 0)
  const cx = posCoord(anchor, 'x_pos') ?? n(position.status_origin_point_x);
  const cz = posCoord(anchor, 'z_pos') ?? n(position.status_origin_point_z);
  const cy = posCoord(anchor, 'y_pos') ?? 0;
  const distanceRaw = Number(scalar(layer(def, 'aoe_distance')) ?? 0);
  const distance = Number.isFinite(distanceRaw) ? distanceRaw : 0;
  const shape = SHAPES[upper(scalar(layer(def, 'aoe_type')))] ?? '';
  const half = SIDE_SPAN.has(shape) ? distance / 2 : distance;
  const f3 = (v: number): string => (Math.round(v * 1000) / 1000).toFixed(3);
  const span = `${upper(input.encounterId)}|${[cx - half, cy, cz, cx + half, cy, cz].map(f3).join(':')}`;

  const height = Number(scalar(layer(def, 'aoe_distance_height')) ?? 0), depth = Number(scalar(layer(def, 'aoe_distance_depth')) ?? 0);
  const zone: Zone = {
    status: { status_aoe_type: upper(scalar(layer(def, 'aoe_type'))), status_origin_point_x: cx, status_origin_point_z: cz,
      status_distance_point_x: cx, status_distance_point_z: cz, status_aoe_distance: distance,
      status_aoe_distance_height: Number.isFinite(height) ? height : 0, status_aoe_distance_depth: Number.isFinite(depth) ? depth : 0, ft3d_depth_bearing: true },
    key: '', dynamic: [cx, cy, cz], holderId: targetId,
  };
  return { span, zone };
}

/** Initial occupants use the same saved anchor and membership reader as the following encounter read. */
export function resolveZoneOccupants(input: ZoneAddInput, zone: Zone): string[] {
  const { encounter, definition: def, layer } = input;
  const targetId = upper(input.targetId);
  const inside = (row: Row): boolean => {
    let occ = input.occupancy(row);
    if (occ === null) return false;
    if (occ === undefined) { // worker pick: an unreadable footprint is the point it stands on
      const x = posCoord(row, 'x_pos') ?? 0, y = posCoord(row, 'y_pos') ?? 0, z = posCoord(row, 'z_pos') ?? 0; // the gate's own reader (Codex A8 P2)
      occ = { kind: 'circle', x, y, r: 0, zMin: z, zMax: z };
    }
    try { return zoneContains(zone, occ); } catch (error) { if (error instanceof ZoneHandBack) return false; throw error; }
  };
  // the follow carrier of a Self-range zone is the caster (search_aoe_primary_target) — it holds the anchor row, never an occupant copy
  const selfCarrier = filled(layer(def, 'aoe_follows_target')) && upper(scalar(layer(def, 'range'))) === SELF_RANGE
    ? upper(creatureId(input.caster) || inventoryId(input.caster)) : '';
  const rider = record(def.attack_ability_arr);
  const flag = (key: string): boolean => filled(layer(def, key)) || (rider !== null && filled(layer(rider, key)));
  const out: string[] = [];
  const seen = new Set([targetId, selfCarrier].filter(Boolean));
  if (!filled(layer(def, 'ability_only_affects_inventory'))) {
    for (const row of rowsOf(encounter.encounter_creatures_arr)) {
      const id = creatureId(row);
      if (!id || seen.has(id)) continue;
      // not on the map: no position at all. Worker pick (owner to review): the legacy gate (pos_coord x_pos / z_pos null) predates the
      // lean compile dropping numeric-zero keys — in a depth-authored encounter a grounded creature's z_pos IS 0 (missing), so the
      // legacy test skipped every creature standing on the floor; missing means zero here, as for the anchor.
      if (!['x_pos', 'y_pos', 'z_pos'].some(k => posCoord(row, k as 'x_pos') !== null)) continue;
      const opp = opposing(input.caster, row, layer);
      if (flag('effect_only_affects_opposing_team_type') && !opp) continue;
      if (flag('effect_only_affects_same_team_type') && opp) continue;
      if (!inside(row)) continue;
      out.push(id); seen.add(id);
    }
  }
  const noInventory = layer(def, 'ability_does_not_effect_inventory') != null || filled(layer(def, 'effect_only_affects_opposing_team_type'));
  if (!noInventory) {
    for (const row of rowsOf(encounter.encounter_inventories_arr)) {
      const id = inventoryId(row);
      if (!id || seen.has(id)) continue;
      const itemType = String(scalar(record(row.items)?.item_type) ?? '').toLowerCase();
      if (itemType === 'map_marker') continue;
      const at = record(row.position);
      if (!at || Object.keys(at).length === 0) continue; // position_arr() empty: not placed
      if (!inside(row)) continue;
      out.push(id); seen.add(id);
    }
  }
  return out;
}
